import { API, API_BASE } from '../../config/api';
import { useState, useRef, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Upload, Loader2, Image, User, Save, X, Plus, Music, Smartphone, Trash2, RefreshCw } from 'lucide-react';
import { AdminQRCode } from '../ShareQR';

const PLAYER_THEMES = [
  { id: 'classic', label: 'Classic', desc: 'Clean minimal player', colors: 'bg-[#141414] border-[#262626]' },
  { id: 'neon', label: 'Neon Glow', desc: 'Cyberpunk neon vibes', colors: 'bg-[#0a0015] border-purple-500/50' },
  { id: 'vinyl', label: 'Vinyl', desc: 'Record player aesthetic', colors: 'bg-[#1a1008] border-amber-800/50' },
  { id: 'ember', label: 'Ember', desc: 'Fiery gradient style', colors: 'bg-[#1a0000] border-red-900/50' },
  { id: 'glass', label: 'Glass', desc: 'Frosted glass modern', colors: 'bg-white/5 border-white/20' },
];


export function SiteTab({ siteSettings, setSiteSettings, profileFormData, setProfileFormData }) {
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingSlide, setUploadingSlide] = useState(false);
  const [uploadingOg, setUploadingOg] = useState(false);
  const [uploadingLinksProfile, setUploadingLinksProfile] = useState(false);
  const [uploadingVoiceTag, setUploadingVoiceTag] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const [savingOg, setSavingOg] = useState(false);
  const heroInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const slideInputRef = useRef(null);
  const ogImageRef = useRef(null);
  const linksProfileRef = useRef(null);
  const voiceTagRef = useRef(null);

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      await api.put(`${API}/site-settings`, { hero_image: imageUrl });
      setSiteSettings(prev => ({ ...prev, hero_image: imageUrl }));
      toast.success('Hero image updated!');
    } catch (err) {
      { const detail = err.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Failed to upload hero image'); }
    } finally {
      setUploadingHero(false);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProfile(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      await api.post(`${API}/artist-profile`, { ...profileFormData, profile_image: imageUrl });
      setProfileFormData(prev => ({ ...prev, profile_image: imageUrl }));
      toast.success('Profile image updated!');
    } catch (err) {
      { const detail = err.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Failed to upload profile image'); }
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleSlideUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const currentImages = siteSettings.slideshow_images || [];
    if (currentImages.length >= 10) {
      toast.error('Maximum 10 slideshow images allowed');
      return;
    }
    setUploadingSlide(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      const updated = [...currentImages, imageUrl];
      await api.put(`${API}/site-settings`, { slideshow_images: updated });
      setSiteSettings(prev => ({ ...prev, slideshow_images: updated }));
      toast.success('Slideshow image added!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingSlide(false);
      if (slideInputRef.current) slideInputRef.current.value = '';
    }
  };

  const removeSlide = async (index) => {
    const currentImages = [...(siteSettings.slideshow_images || [])];
    currentImages.splice(index, 1);
    try {
      await api.put(`${API}/site-settings`, { slideshow_images: currentImages });
      setSiteSettings(prev => ({ ...prev, slideshow_images: currentImages }));
      toast.success('Image removed');
    } catch {
      toast.error('Failed to remove image');
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put(`${API}/site-settings`, {
        marquee_text: siteSettings.marquee_text,
        hero_heading: siteSettings.hero_heading,
        hero_subheading: siteSettings.hero_subheading,
        hero_description: siteSettings.hero_description,
        section_titles: siteSettings.section_titles || {}
      });
      toast.success('Page content saved!');
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSavingContent(false);
    }
  };

  const saveBio = async () => {
    setSavingBio(true);
    try {
      await api.post(`${API}/artist-profile`, { ...profileFormData });
      toast.success('Bio updated!');
    } catch {
      toast.error('Failed to save bio');
    } finally {
      setSavingBio(false);
    }
  };

  const [uploadingArtwork, setUploadingArtwork] = useState({});
  const artworkInputRef = useRef(null);
  const [artworkUploadTarget, setArtworkUploadTarget] = useState(null);

  const handleOgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingOg(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      await api.put(`${API}/site-settings`, { og_image: imageUrl });
      setSiteSettings(prev => ({ ...prev, og_image: imageUrl }));
      toast.success('Share image updated!');
    } catch (err) {
      { const detail = err.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Failed to upload share image'); }
    } finally {
      setUploadingOg(false);
      if (ogImageRef.current) ogImageRef.current.value = '';
    }
  };

  const saveOgSettings = async () => {
    setSavingOg(true);
    try {
      await api.put(`${API}/site-settings`, {
        og_title: siteSettings.og_title || '',
        og_description: siteSettings.og_description || ''
      });
      toast.success('Share settings saved!');
    } catch {
      { const detail = err.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Failed to save share settings'); }
    } finally {
      setSavingOg(false);
    }
  };

  const handleLinksProfileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLinksProfile(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      await api.put(`${API}/site-settings`, { links_profile_image: imageUrl });
      setSiteSettings(prev => ({ ...prev, links_profile_image: imageUrl }));
      toast.success('Links profile image updated!');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploadingLinksProfile(false);
      if (linksProfileRef.current) linksProfileRef.current.value = '';
    }
  };

  const handleArtworkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !artworkUploadTarget) return;
    const { themeId, type } = artworkUploadTarget;
    const key = `${themeId}_${type}`;
    setUploadingArtwork(prev => ({ ...prev, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      const currentArtwork = siteSettings?.player_artwork || {};
      const themeArt = currentArtwork[themeId] || {};
      const updated = { ...currentArtwork, [themeId]: { ...themeArt, [type]: imageUrl } };
      await api.put(`${API}/site-settings`, { player_artwork: updated });
      setSiteSettings(prev => ({ ...prev, player_artwork: updated }));
      toast.success(`${type === 'bg' ? 'Background' : 'Logo'} uploaded for ${PLAYER_THEMES.find(t => t.id === themeId)?.label}`);
    } catch { toast.error('Upload failed'); }
    finally {
      setUploadingArtwork(prev => ({ ...prev, [key]: false }));
      if (artworkInputRef.current) artworkInputRef.current.value = '';
      setArtworkUploadTarget(null);
    }
  };

  const removeArtwork = async (themeId, type) => {
    const currentArtwork = { ...(siteSettings?.player_artwork || {}) };
    if (currentArtwork[themeId]) {
      delete currentArtwork[themeId][type];
      if (Object.keys(currentArtwork[themeId]).length === 0) delete currentArtwork[themeId];
    }
    try {
      await api.put(`${API}/site-settings`, { player_artwork: currentArtwork });
      setSiteSettings(prev => ({ ...prev, player_artwork: currentArtwork }));
      toast.success('Artwork removed');
    } catch { toast.error('Failed to remove artwork'); }
  };

  return (
    <div className="space-y-8" data-testid="site-content">
      <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">SITE SETTINGS</h2>

      {/* Hero Image */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">HERO / COVER IMAGE</h3>
        <p className="text-[#A3A3A3] text-sm">This is the main image displayed on your homepage hero section.</p>
        <div className="flex items-start gap-6">
          <div className="w-48 h-48 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex-shrink-0">
            {siteSettings.hero_image ? (
              <img src={siteSettings.hero_image} alt="Hero" className="w-full h-full object-cover" data-testid="hero-image-preview" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#A3A3A3]"><Image className="w-12 h-12" /></div>
            )}
          </div>
          <div className="space-y-3">
            <input type="file" accept="image/*" ref={heroInputRef} onChange={handleHeroUpload} className="hidden" />
            <Button onClick={() => heroInputRef.current?.click()} disabled={uploadingHero}
              className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="upload-hero-btn">
              {uploadingHero ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Hero Image</>}
            </Button>
            <p className="text-[#666] text-xs">Recommended: Square image, at least 800x800px. Max 5MB.</p>
          </div>
        </div>
      </div>

      {/* Profile Image */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">PROFILE IMAGE</h3>
        <p className="text-[#A3A3A3] text-sm">Your artist photo - shown in the "About" section on your homepage.</p>
        <div className="flex items-start gap-6">
          <div className="w-48 h-48 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex-shrink-0 rounded-full">
            {profileFormData.profile_image ? (
              <img src={profileFormData.profile_image} alt="Profile" className="w-full h-full object-cover" data-testid="profile-image-preview" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#A3A3A3]"><User className="w-12 h-12" /></div>
            )}
          </div>
          <div className="space-y-3">
            <input type="file" accept="image/*" ref={profileInputRef} onChange={handleProfileImageUpload} className="hidden" />
            <Button onClick={() => profileInputRef.current?.click()} disabled={uploadingProfile}
              className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="upload-profile-btn">
              {uploadingProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Profile Image</>}
            </Button>
            <p className="text-[#666] text-xs">Recommended: Square photo, at least 400x400px. Max 5MB.</p>
          </div>
        </div>
      </div>

      {/* Slideshow Images */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">SLIDESHOW IMAGES</h3>
        <p className="text-[#A3A3A3] text-sm">Up to 10 images that scroll horizontally below your hero section. Drag to reorder.</p>
        
        <div className="flex gap-3 flex-wrap">
          {(siteSettings.slideshow_images || []).map((img, i) => (
            <div key={i} className="relative w-32 h-32 bg-[#0A0A0A] border border-[#262626] overflow-hidden group flex-shrink-0">
              <img src={img} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              <button onClick={() => removeSlide(i)}
                className="absolute top-1 right-1 bg-[#FF3B30] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-slide-${i}`}>
                <X className="w-3 h-3" />
              </button>
              <span className="absolute bottom-1 left-1 bg-black/70 text-xs px-1 font-mono">{i + 1}</span>
            </div>
          ))}
          {(siteSettings.slideshow_images || []).length < 10 && (
            <div className="w-32 h-32 border border-dashed border-[#262626] flex items-center justify-center hover:border-[#FF3B30] transition-colors cursor-pointer"
              onClick={() => slideInputRef.current?.click()} data-testid="add-slide-btn">
              {uploadingSlide ? <Loader2 className="w-6 h-6 animate-spin text-[#A3A3A3]" /> : <Plus className="w-6 h-6 text-[#A3A3A3]" />}
            </div>
          )}
        </div>
        <input type="file" accept="image/*" ref={slideInputRef} onChange={handleSlideUpload} className="hidden" />
        <p className="text-xs text-[#666]">{(siteSettings.slideshow_images || []).length}/10 images. Hover to remove.</p>
      </div>

      {/* Editable Page Content */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">HOMEPAGE CONTENT</h3>
        <p className="text-[#A3A3A3] text-sm">Edit the text shown on your public homepage.</p>
        
        <div>
          <Label className="overline">Scrolling Banner Text</Label>
          <Input value={siteSettings.marquee_text || ''} onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_text: e.target.value }))}
            className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="MELODROP • MUSIC • BEATS • DOWNLOADS"
            data-testid="marquee-text-input" />
          <p className="text-xs text-[#666] mt-1">This scrolls across the top of your page</p>
        </div>

        <div>
          <Label className="overline">Subheading (above main heading)</Label>
          <Input value={siteSettings.hero_subheading || ''} onChange={(e) => setSiteSettings(prev => ({ ...prev, hero_subheading: e.target.value }))}
            className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="YOUR MUSIC DESTINATION"
            data-testid="hero-subheading-input" />
        </div>

        <div>
          <Label className="overline">Main Heading</Label>
          <textarea value={siteSettings.hero_heading || ''} onChange={(e) => setSiteSettings(prev => ({ ...prev, hero_heading: e.target.value }))}
            className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none font-bold" rows={3}
            placeholder={"STREAM.\nDOWNLOAD.\nSUPPORT."} data-testid="hero-heading-input" />
          <p className="text-xs text-[#666] mt-1">Use new lines to break the heading. The second line appears in red.</p>
        </div>

        <div>
          <Label className="overline">Description</Label>
          <textarea value={siteSettings.hero_description || ''} onChange={(e) => setSiteSettings(prev => ({ ...prev, hero_description: e.target.value }))}
            className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none" rows={2}
            placeholder="All my music in one place. Stream on your favorite platform or download directly."
            data-testid="hero-description-input" />
        </div>

        <Button onClick={saveContent} disabled={savingContent}
          className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="save-content-btn">
          {savingContent ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Content</>}
        </Button>
      </div>

      {/* Producer Voice Tag */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">PRODUCER VOICE TAG</h3>
        <p className="text-[#A3A3A3] text-sm">Upload your producer tag audio (e.g. "DugOwtBoi Entertainment"). It will be automatically overlaid on every beat you upload.</p>
        <div className="flex items-center gap-4">
          {siteSettings.voice_tag_url && (
            <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] px-3 py-2">
              <Music className="w-4 h-4 text-[#DAA520]" />
              <audio controls src={siteSettings.voice_tag_url.startsWith('/') ? `${API_BASE}${siteSettings.voice_tag_url}` : siteSettings.voice_tag_url} className="h-8" data-testid="voice-tag-player" />
              <button onClick={() => setSiteSettings(prev => ({ ...prev, voice_tag_url: '' }))} className="text-[#666] hover:text-red-500" data-testid="remove-voice-tag">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <input type="file" accept=".mp3,.wav,.m4a,.flac" ref={voiceTagRef} onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setUploadingVoiceTag(true);
            try {
              const fd = new FormData();
              fd.append('file', file);
              const { data } = await api.post(`${API}/upload/audio`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
              setSiteSettings(prev => ({ ...prev, voice_tag_url: `/api/files/${data.path}` }));
              toast.success('Voice tag uploaded! Click Save Content to apply.');
            } catch {
              toast.error('Failed to upload voice tag');
            }
            setUploadingVoiceTag(false);
          }} className="hidden" />
          <Button onClick={() => voiceTagRef.current?.click()} disabled={uploadingVoiceTag}
            variant="outline" className="rounded-none border-[#262626]" data-testid="upload-voice-tag-btn">
            {uploadingVoiceTag ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> {siteSettings.voice_tag_url ? 'Change Tag' : 'Upload Tag'}</>}
          </Button>
        </div>
      </div>

      {/* QR Code for Promo */}
      <AdminQRCode />

      {/* App Button Settings */}
      <AppButtonSettings siteSettings={siteSettings} setSiteSettings={setSiteSettings} />

      {/* Social Share / OG Settings */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">SOCIAL SHARE PREVIEW</h3>
            <p className="text-[#A3A3A3] text-sm">Controls what shows when someone shares <span className="text-[#DAA520] font-mono">iomsl.com</span> on iMessage, Google Messages, WhatsApp, Discord, Instagram DM, Facebook, X, TikTok bio links, etc.</p>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border-l-2 border-l-[#DAA520] px-3 py-2 text-xs text-[#A3A3A3]">
          <p><span className="text-[#DAA520] font-bold">How this works:</span> Save your title, description &amp; image below. The preview updates instantly inside your site. For external link previews (iMessage, WhatsApp, etc.), your changes take effect on the next <span className="text-white font-bold">deploy</span>. After deploying, ask the social app to refresh its cache (X &amp; FB have a debugger tool; iMessage usually refreshes after sharing once or twice).</p>
        </div>

        <div>
          <Label className="overline">Share Title</Label>
          <Input value={siteSettings.og_title || ''} onChange={(e) => setSiteSettings(prev => ({ ...prev, og_title: e.target.value }))}
            className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="DraiMoe — I'm On My 2nd Life | IOMSL"
            data-testid="og-title-input" />
        </div>

        <div>
          <Label className="overline">Share Description</Label>
          <textarea value={siteSettings.og_description || ''} onChange={(e) => setSiteSettings(prev => ({ ...prev, og_description: e.target.value }))}
            className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none" rows={2}
            placeholder="Stream, buy & download music, beats & merch. DugOwtBoi Entertainment."
            data-testid="og-description-input" />
        </div>

        <div>
          <Label className="overline">Share Image</Label>
          <p className="text-xs text-[#666] mb-2">This image shows when someone shares your link. Recommended: 1200x630px.</p>
          <div className="flex items-start gap-6">
            <div className="w-60 h-32 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex-shrink-0">
              {siteSettings.og_image ? (
                <img src={siteSettings.og_image} alt="Share preview" className="w-full h-full object-cover" data-testid="og-image-preview" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#A3A3A3]"><Image className="w-8 h-8" /></div>
              )}
            </div>
            <div className="space-y-3">
              <input type="file" accept="image/*" ref={ogImageRef} onChange={handleOgImageUpload} className="hidden" />
              <Button onClick={() => ogImageRef.current?.click()} disabled={uploadingOg}
                className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="upload-og-image-btn">
                {uploadingOg ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Share Image</>}
              </Button>
            </div>
          </div>
        </div>

        <Button onClick={saveOgSettings} disabled={savingOg}
          className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="save-og-btn">
          {savingOg ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Share Settings</>}
        </Button>

        {/* Live Preview — iMessage style */}
        {(siteSettings.og_title || siteSettings.og_image) && (
          <div className="border-t border-[#262626] pt-4">
            <Label className="overline text-[#DAA520] mb-2 block">Preview — iMessage / Google Messages</Label>
            <div className="bg-gradient-to-b from-[#1a0e0e] to-[#0d0707] p-4 rounded-lg max-w-sm">
              <div className="bg-[#2A1818] rounded-2xl overflow-hidden shadow-lg" data-testid="og-imessage-preview">
                {siteSettings.og_image && (
                  <img src={siteSettings.og_image} alt="" className="w-full h-44 object-cover" />
                )}
                <div className="px-4 py-3">
                  <p className="font-semibold text-[15px] text-white leading-snug line-clamp-2">{siteSettings.og_title || 'DraiMoe — I\'m On My 2nd Life | IOMSL'}</p>
                  <p className="text-[13px] text-[#A8A8A8] mt-1 line-clamp-2 leading-snug">{siteSettings.og_description || 'Stream, buy & download music, beats & merch.'}</p>
                  <p className="text-[12px] text-[#7D7D7D] mt-2 lowercase">iomsl.com</p>
                </div>
              </div>
              <p className="text-[10px] text-[#666] mt-2 italic">This is approximately how recipients will see your link in their texts.</p>
            </div>
          </div>
        )}
      </div>

      {/* Links Page Profile Image */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">LINKS PAGE PROFILE IMAGE</h3>
        <p className="text-[#A3A3A3] text-sm">The profile image shown at the top of your iomsl.com/links page.</p>
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 border-2 border-[#DAA520] overflow-hidden rounded-full flex-shrink-0">
            {(siteSettings.links_profile_image || siteSettings.hero_image) ? (
              <img src={siteSettings.links_profile_image || siteSettings.hero_image} alt="Links profile" className="w-full h-full object-cover" data-testid="links-profile-preview" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#0A0A0A] text-[#A3A3A3]"><Image className="w-6 h-6" /></div>
            )}
          </div>
          <div>
            <input type="file" accept="image/*" ref={linksProfileRef} onChange={handleLinksProfileUpload} className="hidden" />
            <Button onClick={() => linksProfileRef.current?.click()} disabled={uploadingLinksProfile}
              className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="upload-links-profile-btn">
              {uploadingLinksProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Change Image</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Artist Bio */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">ARTIST BIO</h3>
        <p className="text-[#A3A3A3] text-sm">Shown in the "About the Artist" section on your homepage.</p>
        <textarea value={profileFormData.bio || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, bio: e.target.value }))}
          rows={4} className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF3B30] p-3 text-white resize-none outline-none"
          placeholder="Tell your fans about yourself..." data-testid="artist-bio-input" />
        <Button onClick={saveBio} disabled={savingBio}
          className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="save-bio-btn">
          {savingBio ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Bio'}
        </Button>
      </div>

      {/* Social Links */}
      {/* Player Theme Picker */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Music className="w-5 h-5 text-[#FF3B30]" /> PLAYER THEME</h3>
        <p className="text-[#A3A3A3] text-sm">Choose a look for your homepage music player.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {PLAYER_THEMES.map((t) => {
            const selected = (siteSettings?.player_theme || 'classic') === t.id;
            return (
              <button
                key={t.id}
                onClick={async () => {
                  try {
                    await api.put(`${API}/site-settings`, { player_theme: t.id });
                    setSiteSettings(prev => ({ ...prev, player_theme: t.id }));
                    toast.success(`Player theme set to ${t.label}`);
                  } catch { toast.error('Failed to update theme'); }
                }}
                className={`relative p-4 border-2 text-left transition-all ${selected ? 'border-[#FF3B30] ring-1 ring-[#FF3B30]' : 'border-[#262626] hover:border-[#444]'}`}
                data-testid={`player-theme-${t.id}`}
              >
                <div className={`w-full h-8 mb-3 rounded border ${t.colors}`} />
                <p className="text-sm font-bold text-white">{t.label}</p>
                <p className="text-xs text-[#A3A3A3]">{t.desc}</p>
                {selected && <div className="absolute top-2 right-2 w-3 h-3 bg-[#FF3B30] rounded-full" />}
              </button>
            );
          })}
        </div>

        {/* Per-theme artwork uploads */}
        <input type="file" ref={artworkInputRef} accept="image/*" className="hidden" onChange={handleArtworkUpload} />
        <div className="border-t border-[#262626] pt-4 mt-4">
          <h4 className="text-sm font-bold uppercase tracking-wider text-[#A3A3A3] mb-3">CUSTOM ARTWORK PER THEME</h4>
          <p className="text-xs text-[#A3A3A3] mb-4">Upload a background image and/or logo badge for each player skin.</p>
          <div className="space-y-4">
            {PLAYER_THEMES.map((t) => {
              const themeArt = siteSettings?.player_artwork?.[t.id] || {};
              return (
                <div key={t.id} className="bg-[#0A0A0A] border border-[#262626] p-4" data-testid={`artwork-section-${t.id}`}>
                  <p className="text-sm font-bold mb-3">{t.label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Background */}
                    <div>
                      <Label className="overline text-xs">Background Image</Label>
                      <div className="flex items-center gap-3 mt-2">
                        {themeArt.bg ? (
                          <div className="relative w-20 h-12 bg-[#262626] overflow-hidden border border-[#333] flex-shrink-0">
                            <img src={themeArt.bg} alt="bg" className="w-full h-full object-cover" />
                            <button onClick={() => removeArtwork(t.id, 'bg')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-12 bg-[#262626] border border-dashed border-[#444] flex items-center justify-center text-[#555] text-xs">
                            None
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none border-[#262626] text-xs"
                          disabled={uploadingArtwork[`${t.id}_bg`]}
                          onClick={() => { setArtworkUploadTarget({ themeId: t.id, type: 'bg' }); artworkInputRef.current?.click(); }}
                          data-testid={`upload-bg-${t.id}`}
                        >
                          {uploadingArtwork[`${t.id}_bg`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
                        </Button>
                      </div>
                    </div>
                    {/* Logo */}
                    <div>
                      <Label className="overline text-xs">Logo Badge</Label>
                      <div className="flex items-center gap-3 mt-2">
                        {themeArt.logo ? (
                          <div className="relative w-12 h-12 bg-[#262626] overflow-hidden border border-[#333] flex-shrink-0">
                            <img src={themeArt.logo} alt="logo" className="w-full h-full object-contain" />
                            <button onClick={() => removeArtwork(t.id, 'logo')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-[#262626] border border-dashed border-[#444] flex items-center justify-center text-[#555] text-xs">
                            None
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none border-[#262626] text-xs"
                          disabled={uploadingArtwork[`${t.id}_logo`]}
                          onClick={() => { setArtworkUploadTarget({ themeId: t.id, type: 'logo' }); artworkInputRef.current?.click(); }}
                          data-testid={`upload-logo-${t.id}`}
                        >
                          {uploadingArtwork[`${t.id}_logo`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">SOCIAL LINKS</h3>
        <p className="text-[#A3A3A3] text-sm">Add your profile URLs. Only filled-in platforms show on your homepage.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
            { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
            { key: 'twitter', label: 'Twitter / X', placeholder: 'https://x.com/...' },
            { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@...' },
            { key: 'snapchat', label: 'Snapchat', placeholder: 'https://snapchat.com/add/...' },
            { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@...' },
            { key: 'spotify', label: 'Spotify', placeholder: 'https://open.spotify.com/artist/...' },
            { key: 'apple_music', label: 'Apple Music', placeholder: 'https://music.apple.com/...' },
            { key: 'soundcloud', label: 'SoundCloud', placeholder: 'https://soundcloud.com/...' },
            { key: 'tidal', label: 'Tidal', placeholder: 'https://tidal.com/...' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <Label className="text-xs text-[#A3A3A3]">{label}</Label>
              <Input
                value={(profileFormData.social_links || {})[key] || ''}
                onChange={(e) => setProfileFormData(prev => ({
                  ...prev,
                  social_links: { ...prev.social_links, [key]: e.target.value }
                }))}
                className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                placeholder={placeholder}
                data-testid={`social-${key}-input`}
              />
            </div>
          ))}
        </div>
        <Button onClick={saveBio} disabled={savingBio}
          className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="save-socials-btn">
          {savingBio ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Social Links</>}
        </Button>
      </div>
    </div>
  );
}


const COLOR_OPTIONS = [
  { id: 'red', label: 'Red', value: '#FF3B30', text: '#FFFFFF' },
  { id: 'gold', label: 'Gold', value: '#DAA520', text: '#0A0A0A' },
  { id: 'white', label: 'White', value: '#FFFFFF', text: '#0A0A0A' },
  { id: 'green', label: 'Green', value: '#22C55E', text: '#FFFFFF' },
  { id: 'blue', label: 'Blue', value: '#3B82F6', text: '#FFFFFF' },
  { id: 'purple', label: 'Purple', value: '#8B5CF6', text: '#FFFFFF' },
];

function AppButtonSettings({ siteSettings, setSiteSettings }) {
  const [saving, setSaving] = useState(false);
  const [uploadingBgImage, setUploadingBgImage] = useState(false);
  const [uploadingAppIcon, setUploadingAppIcon] = useState(false);
  const bgImageRef = useRef(null);
  const appIconRef = useRef(null);

  const appButton = siteSettings.app_button || {
    enabled: true,
    text: 'Get App',
    color: 'red',
    show_in_nav: true,
    show_in_links: true,
  };

  const appIconUrls = siteSettings.app_icon_urls || {};
  const hasCustomIcon = Object.keys(appIconUrls).length > 0;

  const update = (field, value) => {
    const updated = { ...appButton, [field]: value };
    setSiteSettings(prev => ({ ...prev, app_button: updated }));
  };

  const handleBgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBgImage(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      update('bg_image', imageUrl);
      toast.success('Button image uploaded!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload image');
    } finally {
      setUploadingBgImage(false);
    }
  };

  const handleAppIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAppIcon(true);
    try {
      // Resize image to 512x512 max before sending (prevents huge payloads)
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      // Pre-fill with the configured app background so transparent source images
      // don't "compound" with whatever the old icon was on the user's home screen.
      const bg = (appButton.app_bg_color || '#0A0A0A');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 512, 512);
      const img = new window.Image();
      img.onload = async () => {
        // Draw the source with object-cover behavior: fill the whole 512×512 square
        // (crop overflow) — avoids transparent pillarboxing for non-square inputs.
        const sw = img.naturalWidth || img.width;
        const sh = img.naturalHeight || img.height;
        const scale = Math.max(512 / sw, 512 / sh);
        const dw = sw * scale;
        const dh = sh * scale;
        const dx = (512 - dw) / 2;
        const dy = (512 - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        try {
          const { data } = await api.post(`${API}/app-icon/generate`, { image_base64: base64 }, { timeout: 60000 });
          if (data.sizes_generated > 0) {
            setSiteSettings(prev => ({ ...prev, app_icon_urls: data.icon_urls }));
            toast.success(`App icon set! ${data.sizes_generated} sizes created. Uninstall & reinstall the app to see it.`);
            fetchIconHistory();
          } else {
            toast.error('Icon upload failed — 0 sizes generated. Please try again.');
          }
        } catch (err) {
          const detail = err.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'Failed to generate app icons. Try a smaller image.');
        }
        setUploadingAppIcon(false);
      };
      img.onerror = () => {
        toast.error('Could not read image. Try a different file.');
        setUploadingAppIcon(false);
      };
      img.src = URL.createObjectURL(file);
    } catch {
      toast.error('Failed to read image');
      setUploadingAppIcon(false);
    }
  };

  const handleAppIconRemove = async () => {
    if (!window.confirm('Remove the current app icon? The site will fall back to the default. To see it on phones already installed, uninstall the app and add it back to the home screen.')) return;
    setUploadingAppIcon(true);
    try {
      await api.delete(`${API}/app-icon`);
      setSiteSettings(prev => {
        const next = { ...prev };
        delete next.app_icon_urls;
        return next;
      });
      toast.success('App icon removed — fell back to default. Uninstall & reinstall to refresh on your phone.');
      fetchIconHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to remove icon');
    } finally {
      setUploadingAppIcon(false);
    }
  };


  // Track availability of the native PWA install prompt (captured globally
  // by index.html via beforeinstallprompt). Chrome/Edge fire it once per
  // page load; Safari never fires it (iOS has no programmatic install).
  const [installAvailable, setInstallAvailable] = useState(
    typeof window !== 'undefined' && !!window.__deferredInstallPrompt
  );
  const [installedState, setInstalledState] = useState(false);
  const [iconHistory, setIconHistory] = useState([]);

  const fetchIconHistory = async () => {
    try {
      const { data } = await api.get(`${API}/app-icon/history`);
      setIconHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      // Silent — non-critical UI element
    }
  };

  useEffect(() => { fetchIconHistory(); }, []);
  useEffect(() => {
    const onAvail = () => setInstallAvailable(true);
    const onInstalled = () => { setInstallAvailable(false); setInstalledState(true); };
    window.addEventListener('pwa-install-available', onAvail);
    window.addEventListener('pwa-installed', onInstalled);
    // Also detect display-mode: standalone (already installed)
    try {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        setInstalledState(true);
      }
    } catch (e) { /* noop */ }
    return () => {
      window.removeEventListener('pwa-install-available', onAvail);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const handleTriggerInstall = async () => {
    const ev = window.__deferredInstallPrompt;
    if (!ev) {
      toast.error('Install prompt not available on this device. Use your browser\'s "Add to Home Screen" / "Install app" menu.');
      return;
    }
    try {
      ev.prompt();
      const choice = await ev.userChoice;
      if (choice.outcome === 'accepted') {
        toast.success('Installing… the new icon will appear on your home screen.');
      } else {
        toast.message('Install dismissed. Your icon changes still apply in the browser.');
      }
      window.__deferredInstallPrompt = null;
      setInstallAvailable(false);
    } catch (e) {
      toast.error('Could not open install dialog.');
    }
  };

  const handleRestoreIcon = async (entry) => {
    if (!window.confirm(`Restore the icon from ${new Date(entry.created_at).toLocaleString()}?\nThis will replace your current sitewide app icon.`)) return;
    try {
      const { data } = await api.post(`${API}/app-icon/history/${entry.id}/restore`);
      if (data?.icon_urls) {
        setSiteSettings(prev => ({ ...prev, app_icon_urls: data.icon_urls }));
      }
      toast.success('Icon restored. Force Refresh PWA to see it on installed devices.');
      fetchIconHistory();
    } catch (e) {
      toast.error('Restore failed');
    }
  };

  const handleDeleteHistoryEntry = async (entry) => {
    if (!window.confirm('Remove this icon from history? This cannot be undone.')) return;
    try {
      await api.delete(`${API}/app-icon/history/${entry.id}`);
      setIconHistory(prev => prev.filter(h => h.id !== entry.id));
      toast.success('Removed from history');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handlePwaForceRefresh = async () => {
    if (!window.confirm('Force refresh the PWA? This clears all browser caches and unregisters the service worker so the icon/manifest are re-fetched immediately. The page will reload.')) return;
    try {
      // 1. Unregister every service worker registered for this origin
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      // 2. Delete every Cache Storage bucket
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      toast.success('Caches cleared — reloading…');
      // 3. Hard-reload so the new service worker registers and icons re-fetch
      setTimeout(() => { window.location.reload(); }, 600);
    } catch (e) {
      toast.error('Refresh failed. Try clearing the browser app cache manually.');
    }
  };


  const saveAppButton = async () => {
    setSaving(true);
    try {
      const payload = {
        app_button: siteSettings.app_button || appButton,
      };
      await api.put(`${API}/site-settings`, payload);
      toast.success('App button settings saved!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const selectedColor = COLOR_OPTIONS.find(c => c.id === appButton.color) || COLOR_OPTIONS[0];

  return (
    <div className="bg-[#141414] border border-[#262626] p-6 space-y-5" data-testid="app-button-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#FF3B30]" />
            APP BUTTON
          </h3>
          <p className="text-[#A3A3A3] text-sm mt-1">Customize the "Get App" install button fans see on your site.</p>
        </div>
        <button
          onClick={() => update('enabled', !appButton.enabled)}
          className={`w-12 h-6 rounded-full relative transition-colors ${appButton.enabled ? 'bg-[#FF3B30]' : 'bg-[#262626]'}`}
          data-testid="app-button-toggle"
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${appButton.enabled ? 'left-6' : 'left-0.5'}`} />
        </button>
      </div>

      {appButton.enabled && (
        <>
          {/* Button Text */}
          <div>
            <Label className="overline">Button Text</Label>
            <Input
              value={appButton.text || 'Get App'}
              onChange={(e) => update('text', e.target.value)}
              className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none max-w-xs"
              placeholder="Get App"
              maxLength={20}
              data-testid="app-button-text-input"
            />
            <p className="text-[#666] text-xs mt-1">Max 20 characters. Shows in nav bar and links page.</p>
          </div>

          {/* Button Color */}
          <div>
            <Label className="overline">Button Color</Label>
            <div className="flex gap-3 mt-2">
              {COLOR_OPTIONS.map(color => (
                <button
                  key={color.id}
                  onClick={() => update('color', color.id)}
                  className={`w-10 h-10 border-2 transition-all ${appButton.color === color.id ? 'border-white scale-110' : 'border-[#262626] hover:border-[#666]'}`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                  data-testid={`app-button-color-${color.id}`}
                />
              ))}
            </div>
            <p className="text-[#666] text-xs mt-1">{appButton.bg_image ? 'Color is used as text overlay color when an image is set.' : 'Sets the button background color.'}</p>
          </div>

          {/* Background Image */}
          <div>
            <Label className="overline">Background Image (Optional)</Label>
            <p className="text-[#666] text-xs mt-1 mb-2">Upload a custom image — it becomes the button background with your text on top.</p>
            <input ref={bgImageRef} type="file" accept="image/*" className="hidden" onChange={handleBgImageUpload} data-testid="app-button-bg-upload-input" />
            
            {appButton.bg_image ? (
              <div className="flex items-center gap-4">
                <div className="w-24 h-12 border border-[#262626] overflow-hidden">
                  <img src={appButton.bg_image} alt="Button bg" className="w-full h-full object-cover" data-testid="app-button-bg-preview" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => bgImageRef.current?.click()} variant="outline" size="sm"
                    className="rounded-none border-[#262626] text-xs" disabled={uploadingBgImage} data-testid="app-button-bg-change">
                    {uploadingBgImage ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3 mr-1" /> Change</>}
                  </Button>
                  <Button onClick={() => update('bg_image', '')} variant="outline" size="sm"
                    className="rounded-none border-[#262626] text-xs text-[#FF3B30]" data-testid="app-button-bg-remove">
                    <X className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => bgImageRef.current?.click()} variant="outline"
                className="rounded-none border-[#262626]" disabled={uploadingBgImage} data-testid="app-button-bg-upload">
                {uploadingBgImage ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Image</>}
              </Button>
            )}
          </div>

          {/* Visibility */}
          <div>
            <Label className="overline">Show Button In</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="app-button-nav-toggle">
                <input
                  type="checkbox"
                  checked={appButton.show_in_nav !== false}
                  onChange={(e) => update('show_in_nav', e.target.checked)}
                  className="w-4 h-4 accent-[#FF3B30]"
                />
                <span className="text-sm text-[#E5E5E5]">Navigation Bar</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="app-button-links-toggle">
                <input
                  type="checkbox"
                  checked={appButton.show_in_links !== false}
                  onChange={(e) => update('show_in_links', e.target.checked)}
                  className="w-4 h-4 accent-[#FF3B30]"
                />
                <span className="text-sm text-[#E5E5E5]">Links Page</span>
              </label>
            </div>
          </div>

          {/* App Icon (Home Screen) */}
          <div className="border-t border-[#262626] pt-4">
            <Label className="overline">App Icon (Home Screen)</Label>
            <p className="text-[#666] text-xs mt-1 mb-2">This is the icon fans see on their home screen after installing your app. Upload a square image (at least 512×512). We automatically generate a <b>safe-zone</b> version so Android / iOS home screens won't crop your logo into a circle — the crop only trims the background color. Phones that <b>already</b> installed the old app must uninstall & re-add the app to see it.</p>

            {/* Current-state diagnostic chip */}
            <div className="mb-3 flex items-center gap-2 text-[11px]" data-testid="app-icon-status">
              {hasCustomIcon ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-500/10 border border-green-500/30 text-green-400 font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Custom icon active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Using default (orange) — upload yours
                </span>
              )}
            </div>

            <input ref={appIconRef} type="file" accept="image/*" className="hidden" onChange={handleAppIconUpload} data-testid="app-icon-upload-input" />

            {hasCustomIcon ? (
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <div className="flex items-end gap-3">
                    {['512', '192', '96'].map(size => (
                      appIconUrls[size] && (
                        <div key={size} className="text-center">
                          <img src={appIconUrls[size]} alt={`${size}px icon`}
                            className="border border-[#262626] rounded-lg"
                            style={{ width: size === '512' ? 64 : size === '192' ? 48 : 32, height: size === '512' ? 64 : size === '192' ? 48 : 32 }}
                            data-testid={`app-icon-preview-${size}`} />
                          <p className="text-[10px] text-[#666] mt-1">{size}px</p>
                        </div>
                      )
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => appIconRef.current?.click()} variant="outline" size="sm"
                      className="rounded-none border-[#262626] text-xs" disabled={uploadingAppIcon} data-testid="app-icon-change">
                      {uploadingAppIcon ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</> : <><Upload className="w-3 h-3 mr-1" /> Change Icon</>}
                    </Button>
                    <Button onClick={handleAppIconRemove} variant="outline" size="sm"
                      className="rounded-none border-[#262626] text-xs text-red-400 hover:text-red-300 hover:border-red-400"
                      disabled={uploadingAppIcon} data-testid="app-icon-remove">
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                    <Button onClick={handlePwaForceRefresh} variant="outline" size="sm"
                      className="rounded-none border-[#262626] text-xs" data-testid="pwa-force-refresh"
                      title="Clear browser + service-worker caches so this device picks up the new icon immediately">
                      <RefreshCw className="w-3 h-3 mr-1" /> Force Refresh PWA
                    </Button>
                    <Button onClick={handleTriggerInstall} variant="outline" size="sm"
                      className="rounded-none border-[#262626] text-xs disabled:opacity-50"
                      disabled={!installAvailable}
                      data-testid="pwa-install-prompt"
                      title={installAvailable ? 'Open native install dialog on this device' : (installedState ? 'Already installed on this device — uninstall from your home screen first' : 'Install prompt not available on this browser (iOS, already installed, or not eligible)')}>
                      <Smartphone className="w-3 h-3 mr-1" />
                      {installedState ? 'Installed on this device' : installAvailable ? 'Install on This Device' : 'Install Not Available'}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button onClick={() => appIconRef.current?.click()} variant="outline"
                className="rounded-none border-[#262626]" disabled={uploadingAppIcon} data-testid="app-icon-upload">
                {uploadingAppIcon ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Icons...</> : <><Upload className="w-4 h-4 mr-2" /> Upload App Icon</>}
              </Button>
            )}

            {/* Icon History — last 5 uploads. Shown regardless of whether a
                current icon is active, so users can always restore. */}
            {iconHistory.length > 0 && (
              <div className="pt-4 mt-4 border-t border-[#262626]" data-testid="icon-history-panel">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
                    Icon History <span className="text-[#555]">({iconHistory.length}/5)</span>
                  </p>
                  <p className="text-[9px] text-[#555]">Click any to restore</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {iconHistory.map((h, idx) => (
                    <div key={h.id}
                      className={`group relative flex flex-col items-center ${idx === 0 ? 'ring-1 ring-green-500/40' : ''}`}
                      data-testid={`icon-history-item-${h.id}`}>
                      <button onClick={() => handleRestoreIcon(h)}
                        className="relative overflow-hidden border border-[#262626] hover:border-[var(--accent,#DAA520)] transition-colors"
                        title={`Restore (${new Date(h.created_at).toLocaleString()})`}
                        data-testid={`icon-history-restore-${h.id}`}>
                        <img src={h.thumbnail_url} alt="" className="w-14 h-14 object-cover" />
                        {idx === 0 && (
                          <div className="absolute top-0.5 left-0.5 px-1 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-green-500/90 text-black">Latest</div>
                        )}
                      </button>
                      <span className="text-[9px] text-[#666] mt-1">{new Date(h.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteHistoryEntry(h); }}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#0A0A0A] border border-[#262626] text-[#666] hover:text-red-400 hover:border-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        title="Remove from history"
                        data-testid={`icon-history-delete-${h.id}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* App Name & Colors */}
          <div className="border-t border-[#262626] pt-4 space-y-3">
            <Label className="overline">App Name & Theme</Label>
            <p className="text-[#666] text-xs">Customize the name and colors that appear when the app is installed.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-[#666]">App Name</Label>
                <Input
                  value={appButton.app_name || 'IOMSL — DraiMoe'}
                  onChange={(e) => update('app_name', e.target.value)}
                  className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs"
                  placeholder="IOMSL — DraiMoe"
                  data-testid="app-name-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-[#666]">Short Name</Label>
                <Input
                  value={appButton.app_short_name || 'IOMSL'}
                  onChange={(e) => update('app_short_name', e.target.value)}
                  className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs"
                  placeholder="IOMSL"
                  maxLength={12}
                  data-testid="app-short-name-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-[#666]">Background Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={appButton.app_bg_color || '#0A0A0A'} onChange={(e) => update('app_bg_color', e.target.value)}
                    className="w-8 h-8 cursor-pointer bg-transparent border-0" data-testid="app-bg-color" />
                  <Input value={appButton.app_bg_color || '#0A0A0A'} onChange={(e) => update('app_bg_color', e.target.value)}
                    className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-7 font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-[#666]">Theme Color (Status Bar)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={appButton.app_theme_color || '#0A0A0A'} onChange={(e) => update('app_theme_color', e.target.value)}
                    className="w-8 h-8 cursor-pointer bg-transparent border-0" data-testid="app-theme-color" />
                  <Input value={appButton.app_theme_color || '#0A0A0A'} onChange={(e) => update('app_theme_color', e.target.value)}
                    className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-7 font-mono" />
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <Label className="overline">Preview</Label>
            <div className="mt-2 bg-[#0A0A0A] border border-[#262626] p-4 flex items-center gap-6">
              <div className="text-center">
                <p className="text-[#666] text-xs mb-2">Nav</p>
                {appButton.bg_image ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${appButton.bg_image})`,
                      color: selectedColor.value,
                      textShadow: (appButton.text || '') ? '0 1px 3px rgba(0,0,0,0.8)' : 'none',
                      padding: (appButton.text || '') ? '4px 8px' : '0',
                      width: (appButton.text || '') ? 'auto' : '28px',
                      height: (appButton.text || '') ? 'auto' : '28px',
                    }}>
                    {(appButton.text || '') && <><Smartphone className="w-3.5 h-3.5" /> {appButton.text}</>}
                  </span>
                ) : (
                  <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
                    style={{ color: selectedColor.value }}>
                    {(appButton.text || '') ? <><Smartphone className="w-3.5 h-3.5" /> {appButton.text}</> : <Smartphone className="w-3.5 h-3.5" />}
                  </span>
                )}
              </div>
              <div className="w-px h-8 bg-[#262626]" />
              <div className="text-center">
                <p className="text-[#666] text-xs mb-2">Links Page</p>
                {appButton.bg_image ? (
                  <span className="inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${appButton.bg_image})`,
                      color: selectedColor.value,
                      textShadow: (appButton.text || '') ? '0 1px 4px rgba(0,0,0,0.9)' : 'none',
                      padding: (appButton.text || '') ? '12px 24px' : '0',
                      width: (appButton.text || '') ? 'auto' : '60px',
                      height: (appButton.text || '') ? 'auto' : '60px',
                    }}>
                    {(appButton.text || '') && <><Smartphone className="w-4 h-4" /> {appButton.text}</>}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider"
                    style={{ backgroundColor: selectedColor.value, color: selectedColor.text }}>
                    {(appButton.text || '') ? <><Smartphone className="w-4 h-4" /> {appButton.text}</> : <Smartphone className="w-4 h-4" />}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <Button onClick={saveAppButton} disabled={saving}
        className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="save-app-button-settings">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save App Button</>}
      </Button>
    </div>
  );
}
